import * as Yup from "yup";

import {
  disabilityPensionFormElementsActive,
  incomeDependencyOrDebtResponsibleFormElementsActive,
  workSituationFormElementsActive,
} from "./FormElements/activeFormElements";
import {
  AdviceProductDetailsFormValues,
  ChildDateOfBirth,
  DebtResponsible,
  DisabilityPension,
  EconomicalHealthWish,
  EconomicalSituation,
  Education,
  FamilySituation,
  WorkSituation,
} from "./FormElements/interfaces";

import { BooleanFormType } from "Store/interfaces/FormTypes";
import { subYears } from "date-fns";

export const validationSchema = (
  formTexts: any,
  lang: string,
): Yup.ObjectSchema<AdviceProductDetailsFormValues> => {
  return Yup.object({
    isStayingInNorwayForMoreThanFiveYears: Yup.boolean(),
    dateOfBirth: Yup.string(),
    workSituation: Yup.mixed<WorkSituation>().required(
      formTexts.workSituation.validation.required[lang],
    ),
    education: Yup.mixed<Education>().when("workSituation", {
      is: (workSituation: WorkSituation) =>
        workSituationFormElementsActive(workSituation),
      then: (schema) =>
        schema.required(formTexts.education.validation.required[lang]),
    }),
    familySituation: Yup.mixed<FamilySituation>().required(
      formTexts.familySituation.validation.required[lang],
    ),
    childDatesOfBirth: Yup.array().test(
      "childDatesOfBirth",
      formTexts.childDatesOfBirth.validation.maxAge[lang],
      (childDatesOfBirth?: ChildDateOfBirth[]) => {
        if (childDatesOfBirth === undefined || childDatesOfBirth.length === 0) {
          return true;
        }
        return (
          childDatesOfBirth.find(
            (dateOfBirth) => dateOfBirth.inputFieldError,
          ) === undefined
        );
      },
    ),
    isExpectingChildren: Yup.mixed<BooleanFormType>(),
    income: Yup.number().required(formTexts.income.validation.required[lang]),
    incomeDependency: Yup.mixed<BooleanFormType>().when(
      ["familySituation", "income"],
      {
        is: (familySituation: FamilySituation, income: number) =>
          incomeDependencyOrDebtResponsibleFormElementsActive(
            familySituation,
            income,
          ),
        then: (schema) =>
          schema.required(formTexts.incomeDependency.validation.required[lang]),
      },
    ),
    debt: Yup.number().required(formTexts.debt.validation.required[lang]),
    debtResponsible: Yup.mixed<DebtResponsible>().when(
      ["familySituation", "debt"],
      {
        is: (familySituation: FamilySituation, debt: number) =>
          incomeDependencyOrDebtResponsibleFormElementsActive(
            familySituation,
            debt,
          ),
        then: (schema) =>
          schema.required(formTexts.debtResponsible.validation.required[lang]),
      },
    ),
    disabilityPension: Yup.mixed<DisabilityPension>().when("workSituation", {
      is: (workSituation: WorkSituation) =>
        disabilityPensionFormElementsActive(workSituation),
      then: (schema) =>
        schema.required(formTexts.disabilityPension.validation.required[lang]),
    }),
    economicalSituation: Yup.mixed<EconomicalSituation>().required(
      formTexts.economicalSituation.validation.required[lang],
    ),
    economicalHealthWish: Yup.mixed<EconomicalHealthWish>().required(
      formTexts.economicalHealthWish.validation.required[lang],
    ),
    addCoInsured: Yup.mixed<BooleanFormType>(),
    coInsuredBirthDate: Yup.date().when("addCoInsured", {
      is: BooleanFormType.TRUE,
      then: (schema) =>
        schema
          .typeError("Fødselsdato må oppgis")
          .required("Fødselsdato må oppgis")
          .min(
            subYears(new Date(), 70),
            "Du må være mellom 18 og 70 for å kjøpe forsikring på nett",
          )
          .max(
            subYears(new Date(), 18),
            "Du må være mellom 18 og 70 for å kjøpe forsikring på nett",
          ),
    }),
    situationComment: Yup.string(),
  }).defined();
};
